import {
  DispatchGuideDto,
  DispatchInterface,
  ManifestDetail,
  RouteInterface,
} from '../interfaces'

/**
 * Arma el payload de `POST /Manifiest/CreateManifest` a partir de las guías
 * seleccionadas.
 *
 * PIPELINE
 * --------
 *
 *   DispatchGuideDto[]  ──▶  partición  ──┬──▶  usable   ──▶  1 línea POR PIEZA  ──▶  ManifestDetail[]
 *   (lo que el operador                   │     (piezas > 0)      pieceNumber 1..N          │
 *    tildó en la tabla)                   │                                                 ▼
 *                                         │                                          DispatchInterface
 *                                         └──▶  rejected (piezas 0/null)                    ▲
 *                                                      │                                    │
 *                                                      ▼                          ruta + transporte
 *                                            NO viajan. El llamador                + chofer + precintos
 *                                            TIENE que mostrarlas.
 *
 * POR QUÉ ESTO ES UNA FUNCIÓN Y NO UN useEffect
 * ---------------------------------------------
 * Estas líneas deciden qué mercancía viaja en un camión. Vivían dentro de un
 * efecto de React, o sea que probarlas exigía montar el componente entero. Acá
 * son datos entran, datos salen.
 *
 * EL BUG QUE ARREGLA
 * ------------------
 * El loop original era `for (i = 0; i < (x.totalPiezas ?? 0); i++)`. Con
 * `totalPiezas` en 0 o null corre CERO veces: la guía no aporta ninguna línea y
 * **desaparece del manifiesto sin que nadie se entere**. Y el peso se calculaba
 * como `x.peso / (x.totalPiezas ?? 0)`, o sea división por cero → `Infinity`.
 *
 * Con selección a mano el operador tildaba 5 guías y lo notaba. Con 1500
 * preseleccionadas de un click, no. Por eso esas guías salen por `rejected` en
 * vez de desaparecer.
 */

export interface DispatchPayloadContext {
  routeID: string
  transportID: string
  driverID: string
  seal1: string
  seal2: string
  seal3: string
  /** Para resolver el destino y los nombres a partir de `routeID`. */
  routes: RouteInterface[]
  creationUser: string
  accountID?: string | null
  accountSiteID?: number | null
  buCodeSource: string
}

export interface DispatchPayloadResult {
  payload: DispatchInterface
  /** Guías tildadas que NO entran al manifiesto por no tener piezas. */
  rejected: DispatchGuideDto[]
  /** Total de líneas de detalle. Es el número que hay que medir contra el límite del API. */
  totalPieces: number
}

/**
 * Identidad de una guía de despacho.
 *
 * `GetAllMissingCCW` se vuelve a llamar en cada búsqueda y devuelve OBJETOS
 * NUEVOS. Cualquier cosa que compare por referencia (`includes`, `!==`) queda
 * desincronizada apenas la lista se refresca: el check sigue pintado pero
 * destildar agrega un duplicado, y el manifiesto emite las piezas dos veces.
 *
 * Por eso todo lo que sea selección compara por esta clave, nunca por referencia.
 */
export const guideKey = (guide: DispatchGuideDto): string =>
  guide?.numGuia?.trim().toUpperCase() ?? ''

/** Una guía aporta líneas al manifiesto solo si declara al menos una pieza. */
export const hasPieces = (guide: DispatchGuideDto): boolean =>
  typeof guide?.totalPiezas === 'number' &&
  Number.isFinite(guide.totalPiezas) &&
  guide.totalPiezas > 0

export function buildDispatchPayload(
  selected: DispatchGuideDto[],
  ctx: DispatchPayloadContext
): DispatchPayloadResult {
  const guides = Array.isArray(selected) ? selected : []

  const usable = guides.filter(hasPieces)
  const rejected = guides.filter((g) => !hasPieces(g))

  const manifestDetail: ManifestDetail[] = []

  usable.forEach((guide) => {
    const pieces = guide.totalPiezas as number
    // El peso declarado es el de la guía completa; se reparte entre sus piezas.
    // `pieces` es > 0 por el filtro de arriba, así que esto nunca es Infinity.
    const weightPerPiece = (guide.peso ?? 0) / pieces

    for (let i = 0; i < pieces; i++) {
      manifestDetail.push({
        serviceCode: guide.servicioID?.toString() ?? '',
        bUCodeSource: guide.origen,
        shipmentNumber: guide.numGuia,
        measureUnitTypeID: null,
        pieceNumber: i + 1,
        statusID: 31,
        locationDestinationCode: guide.ciudadDestino,
        locationConsigneeID: null,
        lineSeqNumber: null,
        difference: 0,
        totalPieces: pieces,
        heigh: 0,
        width: 0,
        lenght: 0,
        physicalWeight: weightPerPiece,
        dimensionalWeight: 0,
        barCode: undefined,
        creationUser: ctx.creationUser,
      } as ManifestDetail)
    }
  })

  const destinationRoute = ctx.routes?.find((r) => r.routeID === ctx.routeID)

  const payload = {
    routeID: ctx.routeID,
    transportID: ctx.transportID,
    driver1ID: ctx.driverID,
    seal1: ctx.seal1,
    seal2: ctx.seal2,
    seal3: ctx.seal3,
    manifestDetail,
    statusID: 35,
    manifestTypeID: 1,
    accountID: ctx.accountID,
    // accountSiteID 0 significa "sin sitio", no el sitio número cero.
    accountSiteID: ctx.accountSiteID === 0 ? undefined : ctx.accountSiteID,
    bUCodeSource: ctx.buCodeSource,
    buCodeDestination: destinationRoute?.buCodeConsignee,
    creationUser: ctx.creationUser,
    shippingMethodID: 10,
    shipperName:
      destinationRoute?.buSourceName + '-' + destinationRoute?.buCodeSource,
    consigneeName: destinationRoute?.buConsigneeName,
    isQRCode: false,
    masterShipmentAssociationList: [],
  } as DispatchInterface

  return { payload, rejected, totalPieces: manifestDetail.length }
}
